import React from 'react'
import CustomTooltip from '../../CustomTooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { red, yellow } from '../../../colors'
import { Box, ClickAwayListener, Typography, useTheme } from '@mui/material'
import { useOptionalWarningHighlight } from '../WarningHighlighterContext'
import { type CellWarningConstraintType, CellWarningType, constraintsMapping, DetailHighlightType } from './constant'

interface CellWarningProps {
  rowId?: string
  keyType: CellWarningType
  constraint?: CellWarningConstraintType
  // Several messages render as separate paragraphs in one tooltip
  message: string | string[]
}

const iconStyles = {
  [CellWarningType.WARNING]: { color: yellow[150], Icon: WarningAmberIcon },
  [CellWarningType.ERROR]: { color: red[700], Icon: ErrorOutlineIcon }
}

const CellWarning: React.FC<CellWarningProps> = ({ rowId, keyType, constraint, message }) => {
  const theme = useTheme()
  const icon = iconStyles[keyType]
  const IconComponent = icon.Icon
  const warningHighlight = useOptionalWarningHighlight()
  const messages = Array.isArray(message) ? message : [message]
  // MUI icons are aria-hidden by default and the tooltip title is JSX (so Tooltip adds no label);
  // without this, screen readers skip the icon and its messages entirely.
  const severity = keyType === CellWarningType.ERROR ? 'Error' : 'Warning'
  const ariaLabel = messages.length > 0 ? `${severity}: ${messages.join(' ')}` : severity

  // Highlights fields needing attention based on the constraint type
  const handleClick = (): void => {
    if (!constraint || !warningHighlight || !rowId) return

    const fieldsToHighlightInRow = constraintsMapping[constraint].cell
    const fieldsToHighlightInDetails = constraintsMapping[constraint].outside

    const targetFieldsHighlighted = fieldsToHighlightInRow.every(
      field => warningHighlight.highlightedCells[`${rowId}:${field}`]
    ) && fieldsToHighlightInDetails.every(
      field => warningHighlight.highlightedCells[`${DetailHighlightType.OUTSIDE_TABLE}:${field}`]
    )

    warningHighlight.clear()
    if (!targetFieldsHighlighted) {
      warningHighlight.highlight(rowId, fieldsToHighlightInRow)
      warningHighlight.highlight(DetailHighlightType.OUTSIDE_TABLE, fieldsToHighlightInDetails)
    }
  }

  // Clears all highlights when focus is away from icon
  const handleClickAway = (event: MouseEvent | TouchEvent): void => {
    // Check if the click was on the warning icon itself (ignore as handleClick will clear)
    const target = event.target as HTMLElement
    if (target.closest('[data-warning-icon]')) return

    warningHighlight?.clear()
  }

  // Formats message to add warning information if constraint exists
  const displayedMessage = (
    <>
      {messages.map((text, i) => (
        <Typography key={`${i}-${text}`} sx={{ fontSize: theme.spacing(3), mt: i > 0 ? theme.spacing(2) : 0 }}>{text}</Typography>
      ))}
      {constraint && (
        <Typography sx={{ mt: theme.spacing(2), fontSize: theme.spacing(3), fontWeight: '600' }}>
          Click the icon to highlight relevant fields.
        </Typography>
      )}
    </>
  )

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ mr: 3 }}>
        <CustomTooltip title={displayedMessage} placement="top">
          <IconComponent role='img' aria-hidden={false} aria-label={ariaLabel} onClick={handleClick} sx={{ color: icon.color, fontSize: 22, cursor: 'pointer', fontWeight: '600' }} />
        </CustomTooltip>
      </Box>
    </ClickAwayListener>
  )
}

export default CellWarning
